import type { TokenPayload } from '../types/auth';

const TOKEN_KEY = 'auth_token';
const REFRESH_TOKEN_KEY = 'refresh_token';

export class TokenManager {
  static setToken(token: string): void {
    try {
      localStorage.setItem(TOKEN_KEY, token);
    } catch {
      // Silently handle localStorage errors
    }
  }

  static getToken(): string | null {
    try {
      return localStorage.getItem(TOKEN_KEY);
    } catch {
      // Silently handle localStorage errors
      return null;
    }
  }

  static removeToken(): void {
    try {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(REFRESH_TOKEN_KEY);
    } catch {
      // Silently handle localStorage errors
    }
  }

  static setRefreshToken(refreshToken: string): void {
    try {
      localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    } catch {
      // Silently handle localStorage errors
    }
  }

  static getRefreshToken(): string | null {
    try {
      return localStorage.getItem(REFRESH_TOKEN_KEY);
    } catch {
      // Silently handle localStorage errors
      return null;
    }
  }

  static isTokenExpired(token: string): boolean {
    try {
      const payload = this.decodeToken(token);
      if (!payload || !payload.exp) {
        return true;
      }
      
      // Add 5 minute buffer before expiration
      const bufferTime = 5 * 60 * 1000; // 5 minutes in milliseconds
      const expirationTime = payload.exp * 1000; // Convert to milliseconds
      const currentTime = Date.now();
      
      return currentTime >= (expirationTime - bufferTime);
    } catch {
      // If we can't check expiration, assume it's expired
      return true;
    }
  }

  static decodeToken(token: string): TokenPayload | null {
    try {
      const base64Url = token.split('.')[1];
      if (!base64Url) {
        return null;
      }
      
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      
      return JSON.parse(jsonPayload) as TokenPayload;
    } catch {
      // If we can't decode the token, it's invalid
      return null;
    }
  }

  static isValidToken(token: string): boolean {
    if (!token) {
      return false;
    }
    
    const payload = this.decodeToken(token);
    return payload !== null && !this.isTokenExpired(token);
  }

  static clearAll(): void {
    this.removeToken();
  }
}